'use client'

import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { MarketplaceAssetPurchaseButton } from '@/components/marketplace/marketplace-asset-purchase-button'
import { useHeaderFeatures } from '@/lib/contexts/HeaderFeaturesContext'
import { useUserContext } from '@/lib/contexts/UserContext'
import { sendDownloadUpgradePromptEvent } from '@/lib/analytics/download-upgrade'
import { Asset } from '@/lib/assets/types'
import { MarketplaceAssetPurchaseContextItem } from '@/lib/marketplace/types'
import { cn, generateUpgradeUrl } from '@/lib/utils'

type DownloadRestrictionPromptProps = {
  asset: Asset
  marketplaceContext?: MarketplaceAssetPurchaseContextItem | null
  surface: 'asset_modal' | 'subvariant_download'
  className?: string
}

export function DownloadRestrictionPrompt({ asset, marketplaceContext, surface, className }: DownloadRestrictionPromptProps) {
  const { marketplaceCartEnabled } = useHeaderFeatures()
  const { user } = useUserContext()
  const showMarketplaceAction = marketplaceCartEnabled === true
  const isLoggedIn = Boolean(user)

  useEffect(() => {
    sendDownloadUpgradePromptEvent('download_upgrade_prompt_shown', {
      surface,
      is_logged_in: isLoggedIn,
      refusal_code: 'POLICY_REFUSED_CONSUMPTION',
    })
  }, [isLoggedIn, surface])

  return (
    <div
      className={cn(
        'tw-flex tw-w-[min(22rem,calc(100vw-3rem))] tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-white/15 tw-bg-[#14151c] tw-p-4 tw-text-white tw-shadow-2xl',
        className
      )}
    >
      <p className="tw-text-sm tw-font-medium tw-leading-snug tw-text-white/90">
        Unlock this asset to continue the download:
      </p>
      <div
        className={cn('tw-flex tw-flex-wrap tw-items-center tw-gap-2', !showMarketplaceAction && 'tw-justify-center')}
      >
        <Button
          type="button"
          shape="rounded"
          className="!tw-h-8 !tw-min-w-[9rem] !tw-px-3 !tw-text-xs !tw-font-bold !tw-uppercase"
          onClick={() => {
            sendDownloadUpgradePromptEvent('download_upgrade_prompt_clicked', {
              surface,
              is_logged_in: isLoggedIn,
              refusal_code: 'POLICY_REFUSED_CONSUMPTION',
            })
            const redirectTo = window.location.href
            window.location.href = generateUpgradeUrl(redirectTo)
          }}
        >
          Get Unlimited Access
        </Button>
        {showMarketplaceAction && (
          <>
            <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-white/50">or</span>
            <MarketplaceAssetPurchaseButton
              asset={asset}
              context={marketplaceContext}
              compact
              compactLayout="inline"
              compactVariant="tooltip"
            />
          </>
        )}
      </div>
    </div>
  )
}
